import React, { useEffect, useState, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * CircleProgress — Componente unificado de progreso circular con 12 segmentos animados.
 *
 * Props:
 *   value         — Valor numérico actual
 *   min           — Valor mínimo del rango (default 0)
 *   max           — Valor máximo del rango (default 100)
 *   size          — Tamaño del SVG en px (default 60)
 *   strokeWidth   — Grosor de los segmentos (default 5)
 *   bgColor       — Color de los segmentos inactivos (default '#222')
 *   textColor     — Color del texto interior (default '#B8F1FF')
 *   progressColor — Color de los segmentos activos; pase un string de color plano (default '#B8F1FF')
 *   displayText   — Texto a mostrar dentro del círculo (ej. "42°C", "5A", "67%", "120")
 */
const CircleProgress = ({
    value = 0,
    min = 0,
    max = 100,
    size = 60,
    strokeWidth = 5,
    bgColor = '#222',
    textColor = '#B8F1FF',
    progressColor = '#B8F1FF',
    displayText = '',
}) => {
    const startAngle = -45;
    const segments = 12;
    const segmentAngle = 270 / segments;
    const gapAngle = 3;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const actualSize = isMobile ? Math.max(48, Math.round(size * 0.8)) : size;
    const actualStroke = isMobile ? Math.max(3, Math.round(strokeWidth * 0.7)) : strokeWidth;

    const radius = (actualSize - actualStroke) / 2;
    const center = actualSize / 2;

    const percent = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
    const renderedSegments = Math.round((percent / 100) * segments);

    const polarToCartesian = (cx, cy, r, angle) => {
        const a = (angle - 90) * Math.PI / 180.0;
        return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
    };

    const [activeSegments, setActiveSegments] = useState(renderedSegments);
    const timeouts = useRef([]);

    useEffect(() => {
        timeouts.current.forEach(t => clearTimeout(t));
        timeouts.current = [];
        const delay = 45;
        if (activeSegments < renderedSegments) {
            for (let i = activeSegments; i < renderedSegments; i++) {
                const t = setTimeout(
                    () => setActiveSegments(prev => Math.min(segments, prev + 1)),
                    (i - activeSegments) * delay
                );
                timeouts.current.push(t);
            }
        } else if (activeSegments > renderedSegments) {
            for (let i = activeSegments - 1; i >= renderedSegments; i--) {
                const t = setTimeout(
                    () => setActiveSegments(prev => Math.max(0, prev - 1)),
                    (activeSegments - 1 - i) * delay
                );
                timeouts.current.push(t);
            }
        }
        return () => { timeouts.current.forEach(t => clearTimeout(t)); timeouts.current = []; };
    }, [renderedSegments]);

    const elements = [];
    for (let i = 0; i < segments; i++) {
        const segStartAngle = startAngle + i * segmentAngle + gapAngle / 2;
        const segEndAngle = startAngle + (i + 1) * segmentAngle - gapAngle / 2;
        const s = polarToCartesian(center, center, radius, segStartAngle);
        const e = polarToCartesian(center, center, radius, segEndAngle);
        const large = (segEndAngle - segStartAngle) > 180 ? 1 : 0;
        const d = `M ${s.x} ${s.y} A ${radius} ${radius} 0 ${large} 1 ${e.x} ${e.y}`;
        const isOn = i < activeSegments;
        elements.push(
            <path
                key={i}
                d={d}
                stroke={isOn ? progressColor : bgColor}
                strokeWidth={actualStroke}
                fill="none"
                strokeLinecap="butt"
                style={{ transition: 'opacity 300ms ease', opacity: isOn ? 1 : 0.15 }}
            />
        );
    }

    const fontSize = Math.max(10, actualSize * 0.16);
    const innerWidth = Math.round(actualSize * 0.72);
    const innerHeight = Math.round(fontSize * 1.6);
    const innerX = Math.round((actualSize - innerWidth) / 2);
    const innerY = Math.round(center - innerHeight / 2);

    return (
        <svg width={actualSize} height={actualSize}>
            {elements}
            <foreignObject x={innerX} y={innerY} width={innerWidth} height={innerHeight}>
                <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: textColor,
                        fontWeight: 'bold',
                        fontSize: fontSize,
                    }}
                >
                    <span style={{ color: textColor, fontWeight: 'bold' }}>{displayText}</span>
                </div>
            </foreignObject>
        </svg>
    );
};

export default CircleProgress;
